import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from 'src/common/utils/api';
import {
  ClientDataOperationView,
  ClientDataProposedAction,
  ProposedActionStatus,
} from '../chat/types';

type ClientDataProposalResponse = {
  id: string;
  summary: string;
  status: ProposedActionStatus;
  failureReason: string | null;
  focusedClientId: string | null;
  familyId: string | null;
  operations: ClientDataOperationView[];
};

export function toClientDataProposedAction(
  proposal: ClientDataProposalResponse,
): ClientDataProposedAction {
  return {
    kind: 'CLIENT_DATA',
    proposalId: proposal.id,
    summary: proposal.summary,
    focusedClientId: proposal.focusedClientId,
    operations: proposal.operations,
    status: proposal.status,
    failureReason: proposal.failureReason,
  };
}

export function usePendingClientDataProposals(
  conversationId: string | undefined,
  enabled: boolean,
) {
  const { data, isError } = useQuery<ClientDataProposalResponse[]>({
    queryKey: ['conversations', conversationId, 'client-data-proposals', { status: 'PENDING' }],
    enabled: enabled && !!conversationId,
    // Flag-gating means a 404 during rollout; never retry or surface it as an error state.
    retry: false,
  });
  return { pendingProposals: isError ? [] : (data ?? []) };
}

export function useClientDataProposalDecision(conversationId: string | undefined) {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending } = useMutation<
    { data: ClientDataProposalResponse },
    Error,
    { proposalId: string; decision: 'APPROVE' | 'DENY' }
  >({
    mutationFn: ({ proposalId, decision }) =>
      api.post<ClientDataProposalResponse>(
        `conversations/${conversationId}/client-data-proposals/${proposalId}/decision`,
        { decision },
      ),
    onSuccess: response => {
      if (response.data.status === 'APPLIED') {
        // The approved batch changed client data server-side; drop the whole clients and
        // families caches (lists are keyed ['clients', {params}], so per-id invalidation
        // would miss them) so mounted views re-fetch instead of showing the stale records.
        queryClient.invalidateQueries({ queryKey: ['clients'] });
        queryClient.invalidateQueries({ queryKey: ['families'] });
      }
    },
    // Invalidate on failure too: a 409/404 means the proposal was decided elsewhere, and the
    // pending list must refresh so an orphan-recovered widget disappears rather than staying
    // clickable.
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ['conversations', conversationId, 'client-data-proposals'],
      });
    },
  });
  return { decideClientDataProposal: mutateAsync, isDecidingClientData: isPending };
}
